import { useToasts } from 'react-toast-notifications';
import { observer } from 'mobx-react';
import {
  onItemMove,
  onItemResize,
  RowRenderProps,
  TimelineContentProps,
  TimelineDateHeader,
  TimelineGroupType,
  TimelineItem,
  TimelineItemProps,
  timelineKeys,
  TimelineRow,
} from '@modules/timetable';
import { HSMSLoading } from '@components/shared';
import { useMst } from '@models/Root';
import Timeline, { SidebarHeader, TimelineHeaders } from 'react-calendar-timeline';

export const TimelineContent = observer(
  ({ startTime, endTime, draggableSlots, academicCalendarItems }: TimelineContentProps) => {
    const { addToast } = useToasts();
    const {
      classId,
      timelineItems,
      setTimelineItems,
      timelineGroups,
      loadingTimeSlotConfigurations,
      selectedTimelineItem,
      timetableSlotCounts,
      selectedLocation,
      setSelectedTimelineItem,
      timelineLoading,
      timelineItemConfigVisible,
      setTimelineItemConfigVisible,
      loadingTimelineItems,
    } = useMst().timetable;

    if (loadingTimeSlotConfigurations || loadingTimelineItems) {
      return <HSMSLoading height={184} />;
    }

    const minZoomTime = startTime.valueOf();
    const maxZoomTime = endTime.valueOf();
    return !timelineLoading ? (
      <Timeline
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        keys={timelineKeys}
        defaultTimeStart={startTime}
        defaultTimeEnd={endTime}
        onTimeChange={(
          visibleTimeStart: number,
          visibleTimeEnd: number,
          updateScrollCanvas: (start: number, end: number) => void,
        ) => {
          // Limit scrolling or zooming to the current week
          if (visibleTimeStart < minZoomTime && visibleTimeEnd > maxZoomTime) {
            updateScrollCanvas(minZoomTime, maxZoomTime);
          } else if (visibleTimeStart < minZoomTime) {
            updateScrollCanvas(minZoomTime, minZoomTime + (visibleTimeEnd - visibleTimeStart));
          } else if (visibleTimeEnd > maxZoomTime) {
            updateScrollCanvas(maxZoomTime - (visibleTimeEnd - visibleTimeStart), maxZoomTime);
          } else {
            updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
          }
        }}
        groups={timelineGroups}
        items={timelineItems}
        lineHeight={90}
        itemHeightRatio={0.9}
        canResize="both"
        itemTouchSendsClick
        useResizeHandle
        selected={selectedTimelineItem ? [selectedTimelineItem.id] : []}
        // Open timetable item configuration modal on double click
        onItemClick={() => setTimelineItemConfigVisible(!timelineItemConfigVisible)}
        onItemDeselect={() => {
          setSelectedTimelineItem();
        }}
        onItemMove={async (itemID: string, dragTime: number, newGroupOrder: string) => {
          await onItemMove({
            itemID,
            dragTime,
            newGroupOrder,
            timelineItems,
            addToast,
          });
        }}
        itemRenderer={({ item, itemContext, getItemProps, getResizeProps }: TimelineItemProps) => (
          <TimelineItem
            item={item}
            itemContext={itemContext}
            getItemProps={getItemProps}
            getResizeProps={getResizeProps}
          />
        )}
        onItemResize={(itemID: string, time: number, edge: 'left' | 'right') => {
          onItemResize({
            itemID,
            time,
            edge,
            timelineItems,
          }).then();
        }}
        rowRenderer={({ group }: RowRenderProps) => {
          return (
            <TimelineRow
              draggableSlots={draggableSlots}
              timelineItems={timelineItems}
              setTimelineItems={setTimelineItems}
              group={group}
              addToast={addToast}
              selectedClassId={classId}
              timetableSlotCounts={timetableSlotCounts}
              selectedLocation={selectedLocation}
              academicCalendarItems={academicCalendarItems}
            />
          );
        }}
        horizontalLineClassNamesForGroup={(group: TimelineGroupType) => {
          if (group.id.split('/')[1] === classId) {
            return ['selected-class'];
          }

          return [];
        }}
      >
        <TimelineHeaders>
          <SidebarHeader>
            {() => {
              return (
                <div className="timeline-classes-header">
                  <h4>Classes</h4>
                </div>
              );
            }}
          </SidebarHeader>
          <TimelineDateHeader />
        </TimelineHeaders>
      </Timeline>
    ) : (
      <HSMSLoading height={184} />
    );
  },
);
